import { useEffect, useState, useCallback } from "react";
import { useFormik, Form, FormikProvider } from "formik";
// material
import {
  Stack,
  TextField,
  FormControl,
  Button,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import FormCard from "src/components/common/FormCard";
import SectionHeader from "src/components/common/SectionHeader";
import { LoadingButton } from "@mui/lab";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useMatch,
} from "react-router-dom";
import { userFormValidationSchema } from "../../util/validationSchemas";
import "../../styles/charger.css";
import { useAccess } from "src/hooks/useAccess.hook";
import { getUserDefaultValues } from "./user.constants";
import { Icon } from "@iconify/react";
import { useMutate, useGetDetail, useList } from "src/hooks/useFetch.hook";
import { addUserUrl, getUserDetailUrl, getUserRoleUrl } from "src/react-query/endPoints";
import { Loader } from "../Loader";
import { is } from "src/util/is";
import { FEATURE, COMPANY_TYPE } from "src/util/enums";
import axios from "axios";
import Token from "src/util/Token";
import { useAuth } from "src/hooks/useAuth.hook";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";

export default function UserForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const newUserMatch = useMatch("/users/new");
  const hasUpdateAccess = useAccess(FEATURE.USERS, "U");
  const isReadOnly = !newUserMatch && !hasUpdateAccess;

  const { data, isFetching } = useGetDetail("userDetail", { id });
  const { data: roles } = useList("roles");
  const { data: currentRoleData } = useGetDetail("userRole", {
    id,
    disabled: !id || !!newUserMatch,
  });

  const { mutate, isLoading } = useMutate("users");
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleAssigning, setRoleAssigning] = useState(false);
  const dispatch = useDispatch();

  const isRootAdmin = (() => {
    const parsed = Token.parsedToken();
    return parsed.success &&
      parsed.data?.companyType === COMPANY_TYPE.GRAND_PARENT &&
      parsed.data?.userType === 'ROOT';
  })();

  // The Role attachment is only meaningful at the Grand Parent (GP) company level.
  // For a ROOT super admin it must be hidden while a child company is selected.
  const { user } = useAuth();
  const { teamId } = useSelectedTeam();
  const isGrandParentCompanySelected = String(teamId) === String(user?.company?.id);
  const canAttachRole = isRootAdmin && isGrandParentCompanySelected;

  const onSuccess = () => navigate("/users");

  useEffect(() => {
    if (!currentRoleData?.roleId) return;
    // The dropdown lists one company's copy of each role, while the assigned
    // row can point at another company's copy of the same role. Matching on id
    // alone left the field blank on a user who already had a role — which read
    // as "not assigned" and got it assigned again. Prefer the matching option.
    const options = Array.isArray(roles) ? roles : [];
    const match =
      options.find((r) => r.roleId === currentRoleData.roleId) ||
      options.find((r) => r.roleName === currentRoleData.roleName);
    setSelectedRole(
      match ?? {
        roleId: currentRoleData.roleId,
        roleName: currentRoleData.roleName,
      }
    );
  }, [currentRoleData, roles]);

  const assignRole = useCallback(async (userId, roleId) => {
    await axios.post(getUserRoleUrl(userId), { roleId }, {
      headers: { Authorization: `Bearer ${Token.get()}` },
    });
  }, []);

  const onSubmit = async (userData, { setSubmitting }) => {
    const mutateObject = {
      url: addUserUrl,
      method: "POST",
    };
    if (!is.nullOrUndefined(id) && !newUserMatch) {
      mutateObject.method = "PUT";
      mutateObject.url = getUserDetailUrl(id);
    }
    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: userData,
      },
      {
        onSuccess: async (response) => {
          if (canAttachRole && selectedRole?.roleId) {
            setRoleAssigning(true);
            try {
              const targetUserId = newUserMatch ? response?.data?.userId : id;
              if (targetUserId) {
                await assignRole(targetUserId, selectedRole.roleId);
              }
            } catch (error) {
              // Swallowing this is what made the role look like it never stuck:
              // the save reported success while the assignment had failed, so
              // the same role got picked again on the next visit. Say so.
              dispatch(
                alertSlice.actions.setAlert({
                  open: true,
                  message:
                    error?.response?.data?.message ||
                    "User saved, but the role could not be assigned",
                  severity: "error",
                })
              );
              return; // stay on the form so the role can be retried
            } finally {
              setRoleAssigning(false);
            }
          }
          onSuccess();
        },
      }
    );
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: getUserDefaultValues(),
    validationSchema: userFormValidationSchema,
    onSubmit: onSubmit,
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
  } = formik;

  useEffect(() => {
    if (data) {
      resetForm({ values: data });
    }
  }, [data, resetForm]);

  if (isFetching) {
    return <Loader />;
  }

  if (!newUserMatch && is.empty(data)) {
    navigate("/dashboard");
    return null;
  }

  return (
    <FormCard>
      <FormikProvider value={formik}>
        <Form
          autoComplete="off"
          id="charger"
          noValidate
          onSubmit={handleSubmit}
        >
          <SectionHeader title="User detail" />
          <Grid container spacing={3} className="mb-5">
            <Grid item xs={6} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="User Name"
                  size="small"
                  {...getFieldProps("username")}
                  disabled={isReadOnly}
                  error={Boolean(touched.username && errors.username)}
                  helperText={touched.username && errors.username}
                />
              </FormControl>
            </Grid>
            {canAttachRole && (
              <Grid item xs={6} lg={6}>
                <Autocomplete
                  size="small"
                  options={roles || []}
                  getOptionLabel={(opt) => opt.roleName || ""}
                  isOptionEqualToValue={(opt, val) =>
                    opt.roleId === val.roleId || opt.roleName === val.roleName
                  }
                  value={selectedRole}
                  onChange={(_, val) => setSelectedRole(val)}
                  disabled={isReadOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Role"
                      placeholder="Select a role"
                      size="small"
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={3} className="mb-5">
            <Grid item xs={12} lg={12}>
              <Typography variant="subtitle1" className="mb-2 text-uppercase">
                User Info
              </Typography>
              <Divider className="text-muted" />
            </Grid>
            <Grid item xs={6} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="First Name"
                  size="small"
                  {...getFieldProps("firstName")}
                  disabled={isReadOnly}
                  error={Boolean(touched.firstName && errors.firstName)}
                  helperText={touched.firstName && errors.firstName}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="Last Name"
                  size="small"
                  {...getFieldProps("lastName")}
                  disabled={isReadOnly}
                  error={Boolean(touched.lastName && errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="Email"
                  size="small"
                  {...getFieldProps("email")}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                  disabled={isReadOnly}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="Phone"
                  size="small"
                  {...getFieldProps("phone")}
                  disabled={isReadOnly}
                  error={Boolean(touched.phone && errors.phone)}
                  helperText={touched.phone && errors.phone}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container mt={2}>
            <Grid item xs={6} lg={6}>
              <Stack direction="row" spacing={2}>
                {!isReadOnly && (
                  <>
                    <Button
                      size="medium"
                      type="button"
                      variant="outlined"
                      component={RouterLink}
                      to="/users"
                      startIcon={<Icon icon="eva:close-outline" />}
                      sx={{
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      size="medium"
                      type="submit"
                      variant="contained"
                      disabled={isReadOnly}
                      loading={isSubmitting || isLoading || roleAssigning}
                      startIcon={<Icon icon="eva:save-outline" />}
                      sx={{
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      {isSubmitting || isLoading || roleAssigning ? 'Saving...' : 'Save Changes'}
                    </LoadingButton>
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Form>
      </FormikProvider>
    </FormCard>
  );
}
